/**
 * SimulationAudioExportQueue
 *
 * Shows simulation audio export jobs for admin users.
 * Uses the admin audio exports API with simulationOnly filter.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatDistanceToNow } from 'date-fns';
import api from '../../../services/api';

const SimulationAudioExportQueue = ({ token }) => {
  const [exports, setExports] = useState([]);
  const [stats, setStats] = useState({ pending: 0, processing: 0, completed: 0, failed: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalExports, setTotalExports] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchExports = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/admin/audio-exports?simulationOnly=true&page=${page + 1}&limit=${rowsPerPage}`,
        token
      );

      if (response.data) {
        setExports(response.data.exports || []);
        setStats(response.data.stats || { pending: 0, processing: 0, completed: 0, failed: 0 });
        setTotalExports(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching simulation audio exports:', err);
      setError('Failed to load audio exports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchExports();
  }, [page, rowsPerPage]);

  // Auto-refresh for active jobs
  useEffect(() => {
    const hasActiveJobs = exports.some(e => e.status === 'pending' || e.status === 'processing');
    if (hasActiveJobs) {
      const interval = setInterval(() => fetchExports(false), 10000);
      return () => clearInterval(interval);
    }
  }, [exports]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchExports();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <HourglassEmptyIcon fontSize="small" />;
      case 'processing': return <CircularProgress size={16} />;
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'cancelled': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  const handleDownload = (job) => {
    if (job.fileUrl) {
      window.open(job.fileUrl, '_blank');
    } else {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const downloadUrl = `${baseUrl}/admin/audio-exports/${job.id}/download?token=${token}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `export-${job.clientCode}-${job.id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      await api.delete(`/admin/audio-exports/${jobId}`, token);
      fetchExports();
    } catch (err) {
      console.error('Error deleting export:', err);
      setError('Failed to delete export');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading && exports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Simulation Audio Exports</Typography>
        <Button
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {['pending', 'processing', 'completed', 'failed'].map((status) => (
          <Grid item xs={6} sm={3} key={status}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                  {status === 'completed' ? 'Ready' : status}
                </Typography>
                <Typography
                  variant="h5"
                  color={
                    status === 'completed' ? 'success.main' :
                    status === 'failed' ? 'error.main' :
                    status === 'processing' ? 'primary.main' : 'text.primary'
                  }
                >
                  {stats[status] || 0}
                </Typography>
                {status === 'processing' && (stats.processing || 0) > 0 && (
                  <LinearProgress sx={{ mt: 0.5 }} />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Export Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Run ID</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Messages</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No simulation audio exports yet. Use the "Export Audio" button on a completed run to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              exports.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {job.clientCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDistanceToNow(new Date(job.requestedAt), { addSuffix: true })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={getStatusColor(job.status)}
                      size="small"
                      icon={getStatusIcon(job.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {job.status === 'processing' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress}
                          sx={{ width: 80, mr: 1 }}
                        />
                        <Typography variant="caption">{job.progress}%</Typography>
                      </Box>
                    ) : job.status === 'completed' ? (
                      <Typography variant="body2" color="success.main">Ready</Typography>
                    ) : job.status === 'failed' && job.error ? (
                      <Tooltip title={job.error.message || ''}>
                        <Typography variant="caption" color="error">
                          {job.error.type || 'Error'}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{job.messageCount || '-'}</TableCell>
                  <TableCell>{job.duration || '-'}</TableCell>
                  <TableCell>{formatFileSize(job.fileSize)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {job.status === 'completed' && (
                        <Tooltip title="Download MP3">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(job)}
                            color="primary"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(job.status === 'completed' || job.status === 'failed' || job.status === 'pending') && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(job.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalExports > rowsPerPage && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalExports}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </TableContainer>
    </Box>
  );
};

export default SimulationAudioExportQueue;
